import { motion, AnimatePresence } from 'framer-motion'
import { spacing } from '@/theme'
import { Edit3, User, VideoIcon, LogOut } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'

export function BottomNavigator() {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [visible, setVisible] = useState(true)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const handleScroll = () => {
      const rawScroll = window.scrollY
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      const current = Math.min(rawScroll, maxScroll)

      if (current > lastScrollY.current && current > 20) {
        setVisible(false)
      } else if (current < lastScrollY.current) {
        setVisible(true)
      }

      lastScrollY.current = current
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/auth')
  }

  const isActive = (path: string) =>
    path === '/interacciones'
      ? location.pathname.startsWith('/interacciones')
      : location.pathname === path

  const navigationItems = [
    {
      icon: User,
      label: 'Perfil',
      path: '/dashboard',
      action: 'navigate' as const,
    },
    {
      icon: Edit3,
      label: 'Editar',
      path: '/wizard',
      action: 'navigate' as const,
    },
    {
      icon: VideoIcon,
      label: 'Interacciones',
      path: '/interacciones',
      action: 'navigate' as const,
    },
    {
      icon: LogOut,
      label: 'Salir',
      path: '',
      action: 'logout' as const,
    },
  ]

  return (
    <motion.div
      initial={false}
      animate={{ y: visible ? 0 : '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        // Swapped: Use dashboard header's previous gradient here
        background: `linear-gradient(135deg, ${'#3b82f6'} 0%, ${'#2563eb'} 50%, ${'#1e40af'} 100%)`,
        borderTop: '1px solid rgba(59, 130, 246, 0.4)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 -2px 14px rgba(0, 0, 0, 0.35)',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          maxWidth: '640px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: `${spacing[1]} ${spacing[2]} ${spacing[1]}`,
          gap: spacing[1],
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
          }}
        >
          {navigationItems.map((item, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => item.action === 'logout' ? handleLogout() : navigate(item.path)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1px',
                padding: `${spacing[1]} ${spacing[2]}`,
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: item.action === 'logout' ? '#fca5a5' : isActive(item.path) ? '#fbbf24' : '#bfdbfe',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <item.icon size={20} />
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: item.action === 'logout' ? 600 : isActive(item.path) ? 700 : 500,
                  letterSpacing: '0.2px',
                }}
              >
                {item.label}
              </span>
              {item.action === 'navigate' && isActive(item.path) && (
                <motion.div
                  layoutId="activeIndicator"
                  style={{
                    height: '2px',
                    width: '16px',
                    backgroundColor: '#fbbf24',
                    borderRadius: '2px',
                    marginTop: spacing[1],
                  }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </motion.button>
          ))}
        </div>
<div
  style={{
    marginTop: 8,
    paddingTop: 6,
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    alignItems: 'center',
  }}
>
  <p
    style={{
      margin: 0,
      fontSize: 10,
      color: '#94a3b8',
      textAlign: 'center',
      fontWeight: 400,
    }}
  >
    © {new Date().getFullYear()} WingDrive
  </p>
</div>


      </div>
    </motion.div>
  )
}
